<script lang="ts">
	import { createEventDispatcher, getContext } from "svelte";
	import type {
		DataStore,
		IRow,
		IRowShape,
		TEventDispatcher,
		TMenuItemsFn,
	} from "@xbs/lib-kanban";
	import Icon from "./../lib/Icon.svelte";
	import Menu from "./../lib/Menu.svelte";

	export let row: IRow = {
		id: "default",
		label: "default",
		collapsed: false,
	};
	export let rows: IRow[] = [];
	export let store: DataStore;

	export let collapsable = true;
	export let contentEl: HTMLElement;

	const _ = getContext<any>("wx-i18n").getGroup("kanban");
	const dispatch: TEventDispatcher = createEventDispatcher();

	function handleRowCollapse() {
		dispatch("action", {
			action: "update-row",
			data: {
				id: row.id,
				row: {
					collapsed: !row.collapsed,
				},
			},
		});
	}

	const { readonly, rowShape } = store.getReactive();
	$: edit = $readonly.edit;

	let renaming = false;
	let rowLabel = null;
	$: rowIndex = rows.findIndex(c => c.id === row.id);

	function endRenaming() {
		if (renaming && rowLabel?.trim()) {
			dispatch("action", {
				action: "update-row",
				data: {
					id: row.id,
					row: {
						label: rowLabel,
					},
				},
			});
		}
		renaming = false;
		rowLabel = null;
	}

	function handleRowRename() {
		if (!edit) {
			return;
		}
		renaming = true;
	}
	function handleLabelInput(e: any) {
		rowLabel = e.target.value;
	}
	function handleInputKeypress(e: KeyboardEvent) {
		if (e.charCode === 13) {
			endRenaming();
		}
	}

	function focus(node: HTMLElement) {
		node.focus();
	}

	let menuOpen = false;
	function handleMenuClick() {
		menuOpen = true;
	}

	function handleMove(dir: "up" | "down") {
		const beforeIndex = dir === "up" ? rowIndex - 1 : rowIndex + 2;
		const before = rows[beforeIndex]?.id;

		dispatch("action", {
			action: "move-row",
			data: {
				id: row.id,
				before,
			},
		});
	}

	function handleMenuAction(ev: any) {
		const action = ev.detail.id;
		const menuItem = ev.detail.item;

		if (action === "set-edit") {
			handleRowRename();
		}
		if (action === "delete-row") {
			dispatch("action", {
				action: "delete-row",
				data: {
					id: row.id,
				},
			});
		}
		if (action === "move-row:up") {
			handleMove("up");
		}
		if (action === "move-row:down") {
			handleMove("down");
		}

		if (menuItem.onClick) {
			menuItem.onClick({
				id: action,
				item: menuItem,
				row,
			});
		}
		menuOpen = null;
	}

	let labelEl: HTMLElement;
	let menuEl: HTMLElement;

	const getMenuItems = (
		row: IRow,
		rowIndex: number,
		rows: IRow[],
		rowShape: IRowShape
	) => {
		const menuItems = (
			rowShape.menu.items as TMenuItemsFn<{
				row: IRow;
				rows: IRow[];
				rowIndex: number;
			}>
		)({
			rows,
			rowIndex,
			row: row,
		});

		if (!menuItems || !menuItems.length) {
			return null;
		}

		return menuItems.map(item => {
			return {
				...item,
				label: _(item.label),
			};
		});
	};

	$: menuItems = getMenuItems(row, rowIndex, rows, $rowShape);
	$: isMenuVisible = $rowShape.menu.show && !!menuItems && edit && !renaming;

</script>

<div class="wx-row" class:wx-collapsed={row.collapsed}>
	<div class="wx-label" data-row-header={row.id} bind:this={labelEl}>
		{#if collapsable}
			<div class="wx-label-icon">
				<Icon
					name={row.collapsed ? 'wxi-angle-right' : 'wxi-angle-down'}
					clickable
					click={handleRowCollapse} />
			</div>
			<div class="wx-label-text" on:dblclick={handleRowRename}>
				{#if renaming}
					<input
						type="text"
						class="wx-input"
						value={row.label}
						on:input={handleLabelInput}
						on:keypress={handleInputKeypress}
						on:blur={endRenaming}
						use:focus />
				{:else}{row.label}{/if}
			</div>
			{#if isMenuVisible}
				<div class="wx-menu" bind:this={menuEl}>
					<Icon name="wxi-dots-h" click={handleMenuClick} />
					{#if menuOpen}
						<Menu
							coords={{ x: menuEl.offsetLeft, y: labelEl.offsetTop + labelEl.offsetHeight }}
							data={menuItems}
							on:click={handleMenuAction}
							container={contentEl}
							cancel={() => {
								menuOpen = null;
							}} />
					{/if}
				</div>
			{/if}
		{/if}
		<div class="wx-label-line" />
	</div>
	<div class="wx-content">
		{#if !row.collapsed}
			<slot />
		{/if}
	</div>
</div>

<style>
	.wx-row {
		display: flex;
		flex-direction: column;

		min-width: 100%;
		width: fit-content;

		max-height: 100%;
	}
	.wx-label {
		--wx-icon-color-hover: var(--wx-border-color);
		padding: 0 var(--wx-padding);
		display: flex;
		flex-direction: row;
		align-items: center;

		position: relative;
		z-index: 10;
	}
	.wx-label-text {
		max-width: 50%;
		margin-right: var(--wx-padding);
		margin-left: var(--wx-padding);
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
	.wx-label-line {
		height: 1px;
		background: var(--wx-border-color);
		flex: 1 1 auto;
	}
	.wx-label-icon {
		padding: 0;
		padding-left: var(--wx-padding);
	}
	.wx-content {
		display: flex;
		flex-direction: row;

		max-height: 100%;
	}

	.wx-input {
		padding: var(--wx-input-padding);
		outline: none;
		flex: 1;
		font: var(--wx-font);
		color: var(--wx-color-font);
		width: 100%;
		box-sizing: border-box;

		border: var(--wx-input-border);
		border-radius: var(--wx-input-border-radius);
		background-color: transparent;
	}
	.wx-input:focus {
		border: 1px solid var(--wx-color-primary);
	}

	.wx-menu {
		position: relative;
		margin-right: var(--wx-padding);
	}

</style>
